import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { take, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AUTH_STATE } from '../tokens/auth-state.token';
import { GET_USER_STATUS } from '../tokens/user-status.token';
import { computeEffectiveStatus } from '../models/user-status.model';
import { foreignRoleRedirect } from './role-areas';

/**
 * Guards /profile: requires authenticated + not in profile_pending state.
 * Managers are redirected to /manager.
 * profile_pending (regardless of emailVerified) redirects to /home.
 */
export const profileViewGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          if (status.registrationStatus === 'profile_pending') {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};

/**
 * Guards /profile/edit: requires authenticated.
 * Managers are redirected to /manager.
 * email_unverified (profile_pending + !emailVerified) redirects to /home.
 * All other states (including profile_pending with verified email) allow access.
 */
export const profileEditGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          const effective = computeEffectiveStatus(
            status.registrationStatus,
            user.emailVerified,
          );
          if (effective === 'email_unverified') {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};
